import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { Label } from "~/atoms/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { type DocumentSummary } from "~/api/types";
import { toast } from "react-toastify";
import { formatDate } from "~/helpers/date";
import { getReviewPeriodLabel } from "~/helpers/document";
import { getTierTitle } from "~/helpers/tier";
import { printFullName } from "~/helpers/user";

interface Props {
  trigger: ReactNode;
  documents: DocumentSummary[];
}

export function ExportDataModal({ trigger, documents }: Props) {
  const { t } = useTranslation();
  const [releaseFields, setReleaseFields] = useState({
    id: true,
    title: true,
    status: true,
    reviewFrom: true,
    reviewTo: true,
    fileUpdatedAt: true,
    createdAt: true,
    publishedAt: true,
    adoptedAt: true,
    archivedAt: true,
  });

  const [documentFields, setDocumentFields] = useState({
    owner: true,
    publisher: true,
    tier: true,
    category: true,
    tags: true,
    scheduleReviewPeriod: true,
  });

  const releaseFieldLabels: Record<keyof typeof releaseFields, string> = {
    id: t(($) => $.commons.document.id),
    title: t(($) => $.commons.document.title),
    status: t(($) => $.commons.document.status),
    reviewFrom: t(($) => $.commons.document.reviewFrom),
    reviewTo: t(($) => $.commons.document.reviewTo),
    fileUpdatedAt: t(($) => $.commons.document.fileUpdatedAt),
    createdAt: t(($) => $.commons.document.createdAt),
    publishedAt: t(($) => $.commons.document.publishedAt),
    adoptedAt: t(($) => $.commons.document.adoptedAt),
    archivedAt: t(($) => $.commons.document.archivedAt),
  };

  const documentFieldLabels: Record<keyof typeof documentFields, string> = {
    owner: t(($) => $.commons.document.owner),
    publisher: t(($) => $.commons.document.publisher),
    tier: t(($) => $.commons.document.tier),
    category: t(($) => $.commons.document.category),
    tags: t(($) => $.commons.document.tags),
    scheduleReviewPeriod: t(($) => $.commons.document.recurringPeriod),
  };

  const fieldLabels: Record<string, string> = {
    ...releaseFieldLabels,
    ...documentFieldLabels,
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleExport = () => {
    const selectedData = documents.map((doc) => {
      const release = doc.latestPublishedRelease ?? doc.latestRelease;
      const artifact = release.artifact;
      const result: Record<string, string | number> = {};
      if (releaseFields.id) result.id = doc.id;
      if (releaseFields.title) result.title = artifact.title;
      if (releaseFields.status) result.status = artifact.status;
      if (releaseFields.reviewFrom)
        result.reviewFrom = formatDate(artifact.reviewFrom, "date");
      if (releaseFields.reviewTo)
        result.reviewTo = formatDate(artifact.reviewTo, "date");
      if (releaseFields.fileUpdatedAt)
        result.fileUpdatedAt = formatDate(artifact.fileUpdatedAt, "date");
      if (releaseFields.createdAt)
        result.createdAt = formatDate(release.createdAt, "date");
      if (releaseFields.publishedAt)
        result.publishedAt = formatDate(artifact.publishedAt, "date");
      if (releaseFields.adoptedAt)
        result.adoptedAt = formatDate(release.adoptedAt, "date");
      if (releaseFields.archivedAt)
        result.archivedAt = formatDate(doc.archivedAt, "date");
      if (documentFields.owner)
        result.owner = `${doc.owner.firstName} ${doc.owner.lastName}`;
      if (documentFields.publisher)
        result.publisher = printFullName(doc.publisher);
      if (documentFields.tier) result.tier = getTierTitle(doc.tier?.title, t);
      if (documentFields.category) result.category = doc.category.title;
      if (documentFields.tags)
        result.tags = doc.tags.map((tag) => tag.title).join(", ");
      if (documentFields.scheduleReviewPeriod)
        result.scheduleReviewPeriod =
          getReviewPeriodLabel(doc.scheduleReviewPeriod, t) ?? "";
      return result;
    });

    if (selectedData.length === 0 || !selectedData[0]) {
      toast.info(t(($) => $.toasts.info.noDocumentsFound));
      return;
    }

    const isWindows = navigator.userAgent.includes("Windows");
    const delimiter = isWindows ? "\t" : ";";

    const escapeValue = (value: string | number): string => {
      const str = String(value);
      if (str.includes(delimiter) || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = Object.keys(selectedData[0])
      .map((key) => escapeValue(fieldLabels[key] ?? key))
      .join(delimiter);

    const rows = selectedData.map((row) =>
      Object.values(row).map(escapeValue).join(delimiter),
    );

    const csv = [headers, ...rows].join("\r\n");

    let blob: Blob;
    if (isWindows) {
      const utf16Array = new Uint16Array(csv.length);
      for (let i = 0; i < csv.length; i++) {
        utf16Array[i] = csv.charCodeAt(i);
      }
      const BOM = new Uint8Array([0xff, 0xfe]);
      blob = new Blob([BOM, utf16Array], { type: "text/csv;charset=utf-16le" });
    } else {
      const BOM = "\uFEFF";
      blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "document-metadata-export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setIsOpen(false);
  };

  const toggleReleaseField = (field: string) => {
    setReleaseFields((prev) => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev],
    }));
  };

  const toggleDocumentField = (field: string) => {
    setDocumentFields((prev) => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev],
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-4xl min-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {t(($) => $.commons.workflow.exportMetadata)}
          </DialogTitle>
        </DialogHeader>
        <Card>
          <CardHeader>
            <CardTitle>
              {t(($) => $.commons.workflow.filterDocuments)}
            </CardTitle>
            <CardDescription>
              {t(($) => $.commons.workflow.filterDocumentsDescription)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {t(($) => $.commons.filter.selectedCount, {
              count: documents.length,
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t(($) => $.commons.workflow.selectFields)}</CardTitle>
            <CardDescription>
              {t(($) => $.commons.workflow.selectFieldsDescription)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Object.entries(releaseFields).flatMap(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={value}
                    onCheckedChange={() => toggleReleaseField(key)}
                  />
                  <Label htmlFor={key}>
                    {releaseFieldLabels[key as keyof typeof releaseFieldLabels]}
                  </Label>
                </div>
              ))}
              {Object.entries(documentFields).flatMap(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={value}
                    onCheckedChange={() => toggleDocumentField(key)}
                  />
                  <Label htmlFor={key}>
                    {
                      documentFieldLabels[
                        key as keyof typeof documentFieldLabels
                      ]
                    }
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t(($) => $.commons.workflow.cancel)}
          </Button>
          <Button onClick={handleExport} className="gap-2">
            {t(($) => $.commons.workflow.exportAsCsv)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
